import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getImportBatches, getWhatsAppLogs } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { FileSpreadsheet, CheckCircle, Clock, AlertCircle, GitCompare, MessageSquare, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

function ImportHistorySection() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getImportBatches().then(r => { setBatches(r.data.batches || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="import-history-page">
      <p className="text-boss-text-muted text-sm">{batches.length} import batches</p>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />)}</div>
      ) : batches.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-boss-border">
          <FileSpreadsheet size={40} className="mx-auto text-boss-text-muted mb-4" />
          <h3 className="text-lg font-semibold text-boss-text mb-2">No imports yet</h3>
          <p className="text-sm text-boss-text-muted">Upload a Busy Excel file to create your first import</p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(b => (
            <div key={b._id} className="bg-white rounded-xl p-5 shadow-sm border border-boss-border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    b.status === 'completed' ? 'bg-green-50' : b.status === 'preview' ? 'bg-yellow-50' : 'bg-gray-50'
                  }`}>
                    {b.status === 'completed' ? <CheckCircle size={20} className="text-green-600" /> :
                     b.status === 'preview' ? <Clock size={20} className="text-yellow-600" /> :
                     <AlertCircle size={20} className="text-gray-400" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-boss-text">{b.file_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{b.status}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-boss-text-muted">
                      <span>{formatDateTime(b.upload_time)}</span>
                      <span>{b.total_rows} rows</span>
                      {b.date_range && <span>{b.date_range}</span>}
                    </div>
                  </div>
                </div>

                {b.status === 'completed' && (
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{b.created_rows || 0}</div>
                        <div className="text-[10px] text-boss-text-muted">Created</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">{b.updated_rows || 0}</div>
                        <div className="text-[10px] text-boss-text-muted">Updated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-yellow-600">{b.duplicate_rows || 0}</div>
                        <div className="text-[10px] text-boss-text-muted">Duplicates</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-600">{b.error_rows || 0}</div>
                        <div className="text-[10px] text-boss-text-muted">Errors</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-600">{b.missing_generated || 0}</div>
                        <div className="text-[10px] text-boss-text-muted">Gaps</div>
                      </div>
                    </div>
                    <Link
                      to={`/boss/fa/import/reconciliation/${b._id}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-boss-violet border border-boss-violet/30 hover:bg-boss-violet/5 transition-colors"
                      data-testid={`reconcile-btn-${b._id}`}
                    >
                      <GitCompare size={14} /> Reconcile
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WhatsAppLogsSection() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getWhatsAppLogs({ page, limit: 25, status: statusFilter }).then(r => {
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, statusFilter]);

  return (
    <div className="space-y-4" data-testid="whatsapp-logs-page">
      <div className="flex items-center justify-between">
        <p className="text-boss-text-muted text-sm">{total} messages logged</p>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 rounded-lg border border-boss-border text-sm">
          <option value="">All</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-boss-surface-2">
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Time</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Phone</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Type</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Message</th>
            <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Status</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={5} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>) : logs.length === 0 ? (
              <tr><td colSpan={5} className="py-12 text-center text-boss-text-muted">
                <MessageSquare size={24} className="mx-auto mb-2 opacity-50" />
                No WhatsApp messages logged yet
              </td></tr>
            ) : logs.map(l => (
              <tr key={l._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                <td className="py-2.5 px-4 text-xs text-boss-text-muted">{formatDateTime(l.sent_at)}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{l.phone_number}</td>
                <td className="py-2.5 px-4 text-xs">{l.recipient_type}</td>
                <td className="py-2.5 px-4 max-w-[300px] truncate text-xs">{l.message_text}</td>
                <td className="py-2.5 px-4 text-center">
                  {l.status === 'sent' ? <CheckCircle size={16} className="text-green-500 mx-auto" /> : <XCircle size={16} className="text-red-500 mx-auto" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > 25 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-sm text-boss-text-muted">Page {page}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded border disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => p + 1)} className="p-2 rounded border"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LogsPage() {
  const { isNavAllowed } = useAuth();
  // Same reasoning as BiltyDocsPage: Import History and WhatsApp Logs were
  // gated by different permissions (fa/import/history is BOSS_ADMIN-only
  // today; scl/whatsapp is also granted to SCL_AGENT). Each section keeps
  // its own original check rather than blurring the boundary.
  const canImportHistory = isNavAllowed('/boss/fa/import/history');
  const canWhatsApp = isNavAllowed('/boss/scl/whatsapp');
  const [tab, setTab] = useState(canImportHistory ? 'import' : 'whatsapp');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-boss-text">Logs</h1>
        <p className="text-boss-text-muted text-sm">Import history and WhatsApp message logs</p>
      </div>

      {canImportHistory && canWhatsApp && (
        <div className="flex gap-1 border-b border-boss-border">
          <button
            onClick={() => setTab('import')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'import' ? 'border-boss-violet text-boss-violet' : 'border-transparent text-boss-text-muted hover:text-boss-text'}`}
          >
            Import History
          </button>
          <button
            onClick={() => setTab('whatsapp')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'whatsapp' ? 'border-boss-violet text-boss-violet' : 'border-transparent text-boss-text-muted hover:text-boss-text'}`}
          >
            WhatsApp
          </button>
        </div>
      )}

      {canImportHistory && (tab === 'import' || !canWhatsApp) && <ImportHistorySection />}
      {canWhatsApp && (tab === 'whatsapp' || !canImportHistory) && <WhatsAppLogsSection />}
    </div>
  );
}
